import * as React from "react";
import { Box, Card, Chip, Link, Stack } from "@mui/material";
import Button from "@mui/material/Button";
import Typography from "@mui/material/Typography";
import { ArrowLeftIcon } from "@phosphor-icons/react/dist/ssr/ArrowLeft";
import { PencilSimpleIcon } from "@phosphor-icons/react/dist/ssr/PencilSimple";
import { PlusIcon } from "@phosphor-icons/react/dist/ssr/Plus";
import { TrashIcon } from "@phosphor-icons/react/dist/ssr/Trash";

import { Service, servicesKeys, useDeleteService } from "@/hooks/api/use-services";
import { useCachedTableStateWithUrl } from "@/hooks/use-cached-table-state-with-url";
import { useDialog } from "@/hooks/use-dialog";
import { Breadcrumbs } from "@/components/breadcrumbs/breadcrumbs";
import { RouterLink } from "@/components/core/link";
import { CustomTable } from "@/components/customTable";
import { TableActions } from "@/components/customTable/table-actions";
import { Column, FilterOption, FilterType } from "@/components/customTable/types";
import { DeleteModal } from "@/components/deleteModal/delete-modal";
import { TableAnimation } from "@/components/table-animation/table-animation";

import { CreateService } from "./create";
import { useSearchParams } from "react-router-dom";
import { FILTER_SEPARATOR, SERVICE_TYPE_OPTIONS } from "@/constants";
import { useEffect } from "react";

export function Services(): React.JSX.Element {
  const [searchParams] = useSearchParams();
	const [isCreateServiceDrawerOpen, setIsCreateServiceDrawerOpen] = React.useState(false);
	const [selectedServiceToCreate, setSelectedServiceToCreate] = React.useState<Service | null>(null);

	const {
		items: services,
		isLoading,
		totalItems,
		refetchData: fetchServices,
	} = useCachedTableStateWithUrl({
		queryKey: servicesKeys.lists(),
		endpoint: "/services",
	});

	const deleteServiceMutation = useDeleteService();

	const {
		data: selectedServiceToDelete,
		open: isDeleteServiceModalOpen,
		handleClose: handleDeleteServiceModalClose,
		handleOpen: handleDeleteServiceModalOpen,
	} = useDialog<Service | null>();

	const handleDeleteService = async () => {
		if (selectedServiceToDelete?.id) {
			await deleteServiceMutation.mutateAsync(selectedServiceToDelete.id);
			handleDeleteServiceModalClose();
		}
	};

  useEffect(() => {
		const serviceId = searchParams.get("serviceId");

		if (serviceId) {
			setSelectedServiceToCreate({ id: serviceId } as Service);
			setIsCreateServiceDrawerOpen(true);
		}
	}, []);

	const filtersOptions: FilterOption[] = React.useMemo(() => {
		const typeValue = searchParams.get("type")?.split(FILTER_SEPARATOR)?.[1];

		return [
			{
				name: "type",
				label: "Type",
				value: "",
				type: FilterType.SELECT,
				options: SERVICE_TYPE_OPTIONS,
			},
			{
				name: "pluginId",
				label: "Plugin",
				value: "",
				type: FilterType.FETCH_SELECT,
				getOptionsUrl: typeValue ? `/plugin-services/${typeValue}` : "/plugins",
				fetchLabel: "title",
				fetchValue: "id",
        withoutFetchPagination: true,
			},
		];
	}, [searchParams]);

	const columns: Column[] = React.useMemo(() => {
		return [
			{
				header: {
					id: "title",
					label: "Title",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "title",
			},
			{
				header: {
					id: "Plugin",
					label: "Plugin",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "plugin",
				getCellValue: (row: any) => row.plugin.title,
			},
			{
				header: {
					id: "type",
					label: "Type",
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "type",
        getCellValue: (row: any) => <Chip label={SERVICE_TYPE_OPTIONS.find((option) => option.value === row.type)?.label} size="small" variant="filled" />,
			},
			{
				header: {
					id: "actions",
					label: "",
					width: 100,
					align: "left",
					sx: {
						fontWeight: "600",
					},
				},
				accessorKey: "actions",
				getCellValue: (row: any) => (
					<Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-end" }}>
						<TableActions
							actions={[
								{
									label: "Edit",
									icon: <PencilSimpleIcon width={20} height={20} weight="fill" />,
									onClick: () => {
										setSelectedServiceToCreate(row);
										setIsCreateServiceDrawerOpen(true);
									},
								},
								{
									label: "Delete",
									icon: <TrashIcon width={20} height={20} weight="fill" />,
									sx: { color: "error.main" },
									onClick: () => handleDeleteServiceModalOpen(row),
								},
							]}
						/>
					</Box>
				),
			},
		];
	}, []);

	return (
		<React.Fragment>
			<Box
				sx={{
					maxWidth: "var(--Content-maxWidth)",
					m: "var(--Content-margin)",
					p: "var(--Content-padding)",
					width: "var(--Content-width)",
				}}
			>
				<Stack spacing={4}>
					<Stack direction={"column"} spacing={3}>
						<Breadcrumbs>
							<Link
								color="text.primary"
								component={RouterLink}
								href={"/plugins"}
								sx={{ alignItems: "center", display: "inline-flex", gap: 1, width: "max-content" }}
								variant="subtitle2"
							>
								<ArrowLeftIcon fontSize="var(--icon-fontSize-md)" />
								Plugins
							</Link>

							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								/
							</Typography>
							<Typography variant="subtitle2" color="text.secondary" whiteSpace={"pre"}>
								Services
							</Typography>
						</Breadcrumbs>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={3}
							sx={{ alignItems: { xs: "flex-start", sm: "center" } }}
						>
							<Box sx={{ flex: "1 1 auto" }}>
								<Typography variant="h4">Services Management</Typography>
								<Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
									Configure AI services
								</Typography>
							</Box>
							<Box sx={{ display: "flex", justifyContent: "flex-end" }}>
								<Button
									startIcon={<PlusIcon />}
									variant="contained"
									onClick={() => {
										setSelectedServiceToCreate(null);
										setIsCreateServiceDrawerOpen(true);
									}}
								>
									Create Service
								</Button>
							</Box>
						</Stack>

						<TableAnimation delay={0}>
							<Card>
								<CustomTable
									filtersOptions={filtersOptions}
									columns={columns}
									data={services}
									refetchData={fetchServices}
									isLoading={isLoading}
									totalItems={totalItems}
								/>
							</Card>
						</TableAnimation>
					</Stack>
				</Stack>
			</Box>

			<DeleteModal
				open={isDeleteServiceModalOpen}
				onClose={handleDeleteServiceModalClose}
				onDelete={handleDeleteService}
				title="Delete Service"
				description="Are you sure you want to delete this service?"
				warningText="Any functionality (agents, tools, etc.) that uses this service will stop working and will need to be reconfigured."
			/>

			<CreateService
				isCreateServiceDrawerOpen={isCreateServiceDrawerOpen}
				onCreateDrawerClose={() => {
					setIsCreateServiceDrawerOpen(false);
					setTimeout(() => {
						setSelectedServiceToCreate(null);
					}, 300);
				}}
				serviceId={selectedServiceToCreate?.id || null}
			/>
		</React.Fragment>
	);
}
